import { useEffect, useState, useRef } from 'react';
import { ordersApi, disputesApi, storesApi } from '../api/client';
import clsx from 'clsx';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ShoppingCartIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// TypeScript interfaces for proper typing
interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  store_id: string;
  store_name: string;
  aggregator_order_id: string;
  order_date: string;
  total_amount: number;
  refund_amount: number;
  refund_reason?: string;
  refund_items?: OrderItem[];
  fraud_bucket?: string;
  fraud_score?: number;
  status: OrderStatus;
  dispute?: { id: string };
  created_at: string;
}

type OrderStatus = 'pending_review' | 'classified' | 'disputed' | 'resolved' | 'skipped';

interface FilterState {
  status: string;
  has_refund: string;
}

const BUCKET_COLORS: Record<string, string> = {
  B1: '#3b82f6',
  B2: '#8b5cf6',
  B3: '#f59e0b',
  B4: '#ef4444',
};

interface Store {
  id: string;
  name: string;
  aggregator: string;
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; error: string }[];
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterState>({ status: '', has_refund: '' });
  const [showImport, setShowImport] = useState(false);
  const [selectedStore, setSelectedStore] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchOrders();
    fetchStores();
  }, [filter]);

  async function fetchStores() {
    try {
      const res = await storesApi.list();
      setStores(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  }

  function parseCSV(text: string): Record<string, string>[] {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
    const rows: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      rows.push(row);
    }
    return rows;
  }

  async function handleImport() {
    if (!csvFile || !selectedStore) return;

    setImporting(true);
    setImportResult(null);

    try {
      const text = await csvFile.text();
      const rows = parseCSV(text);

      // Map CSV rows to order format
      const orders = rows.map((row) => ({
        aggregator_order_id: row.order_id || row.aggregator_order_id || `ORD-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        order_date: row.order_date || row.date || new Date().toISOString(),
        total_amount: parseFloat(row.total_amount || row.total || row.subtotal || '0'),
        items: [{ name: 'Imported Order', quantity: 1, price: parseFloat(row.total_amount || row.total || '0') }],
        refund_amount: parseFloat(row.refund_amount || row.refund || '0'),
        refund_reason: row.refund_reason || row.reason || undefined,
      }));

      const res = await ordersApi.import(selectedStore, orders);
      setImportResult(res.data);
      fetchOrders();
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({ imported: 0, skipped: 0, errors: [{ row: 0, error: 'Import failed' }] });
    } finally {
      setImporting(false);
    }
  }

  function closeImportModal() {
    setShowImport(false);
    setCsvFile(null);
    setSelectedStore('');
    setImportResult(null);
  }

  function downloadTemplate() {
    const templateCSV = `order_id,order_date,total_amount,refund_amount,refund_reason,item_names,item_quantities,item_prices
UE-12345678,2024-01-15T18:30:00Z,45.99,12.50,missing_item,"Burger,Fries,Drink","1,1,1","12.99,5.00,3.00"
DD-87654321,2024-01-15T19:15:00Z,32.50,0,,"Pizza,Salad","1,1","24.99,7.51"
GH-11223344,2024-01-16T12:00:00Z,28.75,28.75,wrong_order,"Pasta","1","28.75"
UE-99887766,2024-01-16T20:45:00Z,67.25,15.00,damaged,"Steak,Wine,Dessert","1,2,1","35.99,12.00,7.26"
DD-55443322,2024-01-17T11:30:00Z,22.00,22.00,never_delivered,"Sandwich,Soup","1,1","15.00,7.00"`;

    const blob = new Blob([templateCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'order_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async function fetchOrders() {
    try {
      setLoading(true);
      const params: Record<string, string | boolean | number> = { limit: 50 };
      if (filter.status) params.status = filter.status;
      if (filter.has_refund === 'yes') params.has_refund = true;
      if (filter.has_refund === 'no') params.has_refund = false;

      const res = await ordersApi.list(params);
      setOrders(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }

  async function createDispute(orderId: string) {
    try {
      setCreating(orderId);
      await disputesApi.create(orderId);
      fetchOrders();
    } catch (error) {
      console.error('Failed to create dispute:', error);
    } finally {
      setCreating(null);
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
          <p className="text-slate-500 mt-1">View and manage all incoming orders with refund data</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter.has_refund}
            onChange={(e) => setFilter({ ...filter, has_refund: e.target.value })}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Orders</option>
            <option value="yes">With Refund</option>
            <option value="no">No Refund</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending_review">Pending Review</option>
            <option value="classified">Classified</option>
            <option value="disputed">Disputed</option>
            <option value="resolved">Resolved</option>
          </select>
          <button
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import CSV
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Refund
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Bucket
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-slate-900">
                      {order.aggregator_order_id?.slice(0, 15) || order.id.slice(0, 8)}...
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-900">{order.store_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-600">
                      {new Date(order.order_date).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-900">
                      {formatCurrency(order.total_amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {order.refund_amount > 0 ? (
                      <span className="text-sm font-medium text-red-600">
                        {formatCurrency(order.refund_amount)}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {order.fraud_bucket ? (
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${BUCKET_COLORS[order.fraud_bucket]}15`,
                          color: BUCKET_COLORS[order.fraud_bucket],
                        }}
                      >
                        {order.fraud_bucket}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-6 py-4">
                    {order.refund_amount > 0 && order.status === 'classified' && !order.dispute ? (
                      <button
                        onClick={() => createDispute(order.id)}
                        disabled={creating === order.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors disabled:opacity-50"
                      >
                        {creating === order.id ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <PlusCircleIcon className="w-4 h-4" />
                            Create Dispute
                          </>
                        )}
                      </button>
                    ) : order.dispute ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium">
                        <CheckCircleIcon className="w-4 h-4" />
                        Disputed
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                        <ShoppingCartIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-slate-900 font-medium">No orders found</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {filter.status || filter.has_refund
                            ? 'Try adjusting your filters'
                            : 'Import orders via CSV to get started'}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowImport(true)}
                        className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
                      >
                        <ArrowUpTrayIcon className="w-4 h-4" />
                        Import CSV
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Import Orders</h3>
              <button
                onClick={closeImportModal}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {importResult ? (
                <div className="space-y-4">
                  <div className={clsx(
                    'p-4 rounded-xl',
                    importResult.imported > 0 ? 'bg-emerald-50' : 'bg-amber-50'
                  )}>
                    <p className="font-medium text-slate-900">Import Complete</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {importResult.imported} orders imported, {importResult.skipped} skipped
                    </p>
                    {importResult.errors.length > 0 && (
                      <div className="mt-3 text-sm text-red-600">
                        <p className="font-medium">Errors:</p>
                        {importResult.errors.slice(0, 3).map((e, i) => (
                          <p key={i}>Row {e.row}: {e.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={closeImportModal}
                    className="w-full py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Select Store</label>
                    <select
                      value={selectedStore}
                      onChange={(e) => setSelectedStore(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Choose a store...</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name} ({store.aggregator})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-slate-700">CSV File</label>
                      <button
                        onClick={downloadTemplate}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                        Download Template
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-6 border-2 border-dashed border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                    >
                      {csvFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <DocumentTextIcon className="w-8 h-8 text-blue-500" />
                          <div className="text-left">
                            <p className="font-medium text-slate-900">{csvFile.name}</p>
                            <p className="text-sm text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <ArrowUpTrayIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload CSV file</p>
                          <p className="text-xs text-slate-400 mt-1">
                            order_id, order_date, total_amount, refund_amount, refund_reason
                          </p>
                        </div>
                      )}
                    </button>
                  </div>

                  <button
                    onClick={handleImport}
                    disabled={!csvFile || !selectedStore || importing}
                    className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30"
                  >
                    {importing ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Importing...
                      </span>
                    ) : (
                      'Import Orders'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {orders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <SummaryCard
            label="Total Orders"
            value={orders.length.toString()}
            icon={ShoppingCartIcon}
            color="blue"
          />
          <SummaryCard
            label="With Refunds"
            value={orders.filter((o) => o.refund_amount > 0).length.toString()}
            icon={ExclamationTriangleIcon}
            color="amber"
          />
          <SummaryCard
            label="Classified"
            value={orders.filter((o) => o.fraud_bucket).length.toString()}
            icon={ExclamationTriangleIcon}
            color="purple"
          />
          <SummaryCard
            label="Disputed"
            value={orders.filter((o) => o.dispute).length.toString()}
            icon={CheckCircleIcon}
            color="emerald"
          />
        </div>
      )}
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'amber' | 'purple' | 'emerald';
}

function SummaryCard({ label, value, icon: Icon, color }: SummaryCardProps) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
      <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', colors[color])}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    pending_review: { bg: 'bg-amber-100', text: 'text-amber-700' },
    classified: { bg: 'bg-blue-100', text: 'text-blue-700' },
    disputed: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    skipped: { bg: 'bg-slate-100', text: 'text-slate-700' },
  };

  const style = styles[status] || styles.pending_review;

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize',
        style.bg,
        style.text
      )}
    >
      {status.replace('_', ' ')}
    </span>
  );
}
